/**
 * When developing _locally_ (and only locally!) we show all applications to
 * the developer. NO EXCEPTION: We are not going to do this in any higher
 * environment.
 *
 * Show a colored dot to the developer that indicates the status/value of the
 * application Feature Flag. This component is never seen by our
 * customers/clients and is our little secret 😉
 *
 * We need to account for just three states:
 *
 * - Not Visible, Not Routable → Red
 * - Not Visible, Routable → Orange
 * - Visible, Not Routable → Gray
 */

import { createRef } from "react";
import { GKTooltip } from "@granular/gds";

const COLOR_MAP = {
  red: "var(--bs-red)",
  orange: "var(--bs-orange)",
  gray: "var(--bs-gray)",
};

export type DotColor = keyof typeof COLOR_MAP;

/**
 * Generate the dot color and text based on visibility/routability.
 *
 * NOTE: Do not get carried away with trying to I18n this. These dots are only
 * visible to developers!
 */
export const getDotColorAndText = (
  visible: boolean,
  routable: boolean,
  hasPermission: boolean,
) => {
  if (!visible && !routable) {
    return ["red", "Hidden: 𝙉𝙤𝙩 Visible, 𝙉𝙤𝙩 Routable"];
  }
  if (visible && !routable) {
    return ["gray", "Grayed out: Visible, 𝙉𝙤𝙩 Routable"];
  }
  if (!visible && routable) {
    return ["orange", "Hidden: 𝙉𝙤𝙩 Visible, Routable"];
  }
  if (!hasPermission) {
    return ["red", "No permission"];
  }
  return [undefined, undefined, undefined];
};

const Dot: React.FC<{
  color: DotColor;
  toolTipText?: string;
}> = ({ toolTipText, color = COLOR_MAP.red }) => {
  const ref = createRef<HTMLDivElement>();

  return (
    <>
      <div
        ref={ref}
        style={{
          backgroundColor: COLOR_MAP[color as DotColor],
          borderRadius: "50%",
          display: "inline-block",
          width: "0.65rem",
          minWidth: "0.65rem",
          height: "0.65rem",
          marginRight: "5px",
        }}
      />
      {toolTipText && (
        <GKTooltip target={ref} placement="auto">
          {toolTipText}
        </GKTooltip>
      )}
    </>
  );
};

export default Dot;
